import React, { useEffect } from 'react';
import clsx from 'clsx'
import format from 'date-fns/format'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  Plugin,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
// import clsx from 'clsx';
import styles from './Chart.module.scss';

let chartMidValue = 0

const getOrCreateTooltip = (chart: any) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.classList.add(styles.tooltip)
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const externalTooltipHandler = (context: any, cb?: (v: number) => void) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  if (tooltip.body) {
    const text = document.createTextNode(tooltip.title);

    // Remove old children
    while (tooltipEl?.firstChild) {
      tooltipEl.firstChild.remove();
    }

    // Add new children
    tooltipEl.appendChild(text);

    if (cb) cb(tooltip.dataPoints?.[0]?.raw)
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
  tooltipEl.style.top = `${positionY + tooltip.caretY + ((context.tooltip.dataPoints[0].raw >= chartMidValue) ? 40 : 0)}px`;
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding = `${tooltip.options.padding}px ${tooltip.options.padding}px`;
  tooltipEl.style.zIndex = '10';
};

const plugins: Plugin[] = [
  {
    id: 'tooltipLine',
    afterDraw: (chart: any) => {
      // eslint-disable-next-line no-underscore-dangle
      const activeTooltips = chart.tooltip?._active
      if (activeTooltips?.length) {
        const { x, y, $context: { raw } } = (activeTooltips[0]?.element || {});
        const { ctx } = chart;
        ctx.save();
        ctx.setLineDash([8, 4])
        ctx.beginPath();
        if (raw >= chartMidValue) {
          ctx.moveTo(x, chart.canvas.height - 30);
        } else {
          ctx.moveTo(x, 44);
        }
        ctx.lineTo(x, y);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.stroke();
        ctx.setLineDash([])

        ctx.beginPath();
        ctx.arc(x, y, 5, 0, 2 * Math.PI, false);
        ctx.fillStyle = '#000';
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#fff';
        ctx.stroke();

        ctx.restore();
      }
    },
  },
  {
    id: 'currentLine',
    afterDraw: (chart: any, _, options: any) => {
      if (!options?.lineValue) return null
      const { ctx } = chart;
      const {
        bottom, top, max, min,
      } = chart.scales.y
      const curY = bottom - ((bottom - top) * ((options.lineValue - min) / (max - min)))
      ctx.save();
      ctx.setLineDash([8, 4])
      ctx.beginPath();
      ctx.moveTo(0, curY);
      ctx.lineTo(chart.canvas.width, curY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.stroke();
      ctx.setLineDash([])
      ctx.restore();
      return null
    },
  },
];

function tooltipPositioner(elements) {
  // @ts-ignore
  const self: any = this
  return ({
    x: elements?.[0]?.element?.x || 0,
    y: (elements?.[0]?.element?.$context?.raw >= chartMidValue)
      ? (self?.chart?.height || 0) - 40 - 30
      : 20,
    caretY: 0,
    height: '0px',
  })
}

function Chart({
  className,
  curValue, // eslint-disable-line
  data,
  onSelect,
  setMouseIn,
  setIsPremarket,
  setIsAfterHours,
  showDays = false,
  showTime = true,
  isYear = false,
  fall = false,
  style,
  isCurrency,
  isCrypto,
  isSmall,
  fullSize = false,
  isShortAsset = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  isEtf = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  chartDotsCount = undefined,
}: {
  className?: string,
  curValue?: number | null,
  data: any[],
  onSelect: (v: { price: number, time: string } | null) => void,
  setMouseIn: (v: boolean) => void,
  setIsPremarket?: (v: boolean) => void,
  setIsAfterHours?: (v: boolean) => void,
  showDays?: boolean,
  showTime?: boolean,
  isYear?: boolean,
  fall?: boolean
  style?: any
  isCurrency?: boolean
  isCrypto?: boolean
  isSmall?: boolean
  fullSize?: boolean,
  isShortAsset?: boolean,
  isEtf?: boolean,
  chartDotsCount?: undefined | number
}) {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Filler,
  );

  // @ts-ignore
  Tooltip.positioners.tooltipPositioner = tooltipPositioner;

  const externalTooltipHandlerWithCb = (context: any) => externalTooltipHandler(
    context,
    (v: number) => {
      onSelect({ price: v, time: context.tooltip.title[0] })
    },
  )

  const padding = () => {
    const defaultPadding = {
      top: 10,
      bottom: 10,
      left: 95,
      right: 95,
    };

    if (!fullSize) {
      return 10;
    }

    if (window.matchMedia('(max-width: 500px)').matches) {
      return {
        top: 10,
        bottom: 10,
        left: 12,
        right: 12,
      };
    }

    if (window.matchMedia('(max-width: 1020px)').matches) {
      return {
        top: 10,
        bottom: 10,
        left: 40,
        right: 40,
      };
    }

    if (window.matchMedia('(min-width: 1920px)').matches) {
      const extraPadding = Math.round((window.innerWidth - 1920) / 2);
      return {
        top: 10,
        bottom: 10,
        left: 127 + extraPadding,
        right: 127 + extraPadding,
      };
    }

    if (window.matchMedia('(min-width: 1440px)').matches) {
      const scale = window.innerWidth / 1440;
      return {
        top: 10,
        bottom: 10,
        left: Math.round(95 * scale),
        right: Math.round(95 * scale),
      };
    }

    return defaultPadding;
  };

  const dataValues = (data || []).map((v) => Number(v.close));

  const labels = data.map((v) => {
    if (isSmall) return '';

    const newYorkTime = v.datetime?.replace('+00:00', isCrypto ? '-04:00' : '-05:00');
    const newYorkDate = new Date(newYorkTime).toLocaleString('en-US', !isCrypto ? { timeZone: 'America/New_York' } : {});
    const date = new Date(newYorkDate);

    if (isYear) {
      return new Date(v.datetime).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    if (isCurrency) date.setHours(date.getHours() - 4);

    return format(date, `${showDays ? 'LLL dd ' : ''}${showTime ? 'p' : ''}`);
  });

  let preMarketEndIndex = data.findIndex((item) => item.datetime.includes('T09:30'));
  if (preMarketEndIndex === -1) preMarketEndIndex = dataValues.length - 1;

  let afterHoursStartIndex = data.findIndex((item) => item.datetime.includes('T16:00'));
  if (afterHoursStartIndex === -1) afterHoursStartIndex = labels.length;

  if (isCurrency) {
    preMarketEndIndex = -1;
    afterHoursStartIndex = labels.length;
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 200,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        position: 'tooltipPositioner',
        intersect: false,
        mode: 'index',
        cornerRadius: 20,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        titleColor: '#000',
        caretSize: 0,
        titleAlign: 'center',
        filter: (tooltipItem: any) => !tooltipItem?.datasetIndex,
        external: (context) => {
          externalTooltipHandlerWithCb(context)
          /* eslint no-underscore-dangle: ["error", { "allow": ["_active"] }] */
          const index = context?.tooltip?._active?.[0]?.index;
          if (index !== undefined) {
            if (setIsPremarket) {
              setIsPremarket(index < preMarketEndIndex);
            }
            if (setIsAfterHours) {
              setIsAfterHours(index >= afterHoursStartIndex);
            }
          }
        },
      },
      currentLine: {
        lineValue: curValue,
      },
    },
    layout: {
      padding,
    },
    scales: {
      y: {
        min: curValue && dataValues.length ? Math.min(...dataValues, curValue) : null,
        max: curValue && dataValues.length ? Math.max(...dataValues, curValue) : null,
        border: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
          drawTicks: false,
        },
      },
      x: {
        border: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
          drawTicks: false,
        },
      },
    },
    animation: {
      duration: 0, // Reduce all animations time
    },
    spanGaps: true,
    elements: {
      point: {
        radius: 0, // default to disabled in all datasets
      },
    },
    normalized: true,
  };

  const minMax = (data || []).reduce((acc: any, v: any) => {
    const res = acc
    if (!acc.min || (acc.min > v.close)) {
      res.min = v.close
    }
    if (!acc.max || (acc.max < v.close)) {
      res.max = v.close
    }
    return res
  }, { min: 0, max: 0 })

  chartMidValue = (minMax.max + minMax.min) / 2

  const dataLabels = () => {
    let values = labels;
    const dotCount = chartDotsCount || 120

    if (!showDays && values?.length < dotCount) {
      const nulls = new Array(dotCount).fill('')
      values = nulls.map((value, i) => values[i] || '')
    }
    return values;
  }

  const chartData = {
    labels: dataLabels(),
    datasets: [
      {
        label: '',
        data: dataValues,
        borderWidth: 2,
        borderColor: !fall ? '#6FD964' : '#FF4D30',
        segment: {
          borderColor: (ctx) => {
            if (!fall) {
              if ((ctx?.p1DataIndex > preMarketEndIndex && ctx?.p1DataIndex <= afterHoursStartIndex)
                || isCurrency || showDays) {
                return '#6FD964'
              }
              return '#6fd96478'
            }
            if ((ctx?.p1DataIndex > preMarketEndIndex && ctx?.p1DataIndex <= afterHoursStartIndex)
              || isCurrency || showDays) {
              return '#ff4d30'
            }
            return '#ff4d3078'
          },
          // borderDash: ctx => skipped(ctx, [6, 6]),
        },
        backgroundColor: !fall ? 'rgba(111, 217, 100, 0.2)' : 'rgba(255, 77, 48, 0.2)',
        fill: false,
        pointRadius: 0,
        order: 1,
      },
    ],
  };

  const handleMouseLeave = () => {
    setMouseIn(false)
    if (setIsPremarket) {
      setIsPremarket(dataValues.length - 1 < preMarketEndIndex)
    }
    if (setIsAfterHours) {
      setIsAfterHours(dataValues.length - 1 >= afterHoursStartIndex)
    }
    onSelect(null)
  }

  useEffect(() => {
    handleMouseLeave()
  }, [])

  return (
    <>
      <div className={styles.borderDashed} />
      <div
        className={clsx(styles.container, className)}
        style={style}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={() => setMouseIn(true)}
      >
        <Line
          // @ts-ignore
          options={options}
          data={chartData}
          plugins={plugins}
        />
      </div>
    </>
  )
}
export default Chart
